import React, { useContext, useState } from "react";
import {
  ApiErrorHandler,
  IMuteNotificationOption,
  OrganizationContext,
  useTranslation,
} from "hhn-commons";
import Toggle from "../../../common/Toggle/Toggle";
import MuteNotifications from "../../../message/messageHeader/patientGroupHeader/MuteNotifications";
import { useSnackbar } from "notistack";
import {
  IGroupInfo,
  INonPatientChats,
  IPatientChats,
} from "@home-health-notify/domain";
import dayjs from "dayjs";
import { IMuteNotificationsRequest } from "@home-health-notify/models";

const MuteNotificationsToggle = ({ groupInfo }: { groupInfo: IGroupInfo }) => {
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation();
  const tNs = (key: string) => t(key, { ns: "groupChatInfo" });
  const { selectedOrgUser } = useContext(OrganizationContext);
  const areNotificationsMuted = groupInfo.settings?.mute.value || false;

  const [isMuteNotificationsDialogOpen, setIsMuteNotificationsDialogOpen] =
    useState<boolean>(false);
  const [muteDuration, setMuteDuration] = useState<IMuteNotificationOption>();

  const closeMuteNotificationsDialog = () => {
    setIsMuteNotificationsDialogOpen(false);
    setMuteDuration(undefined);
  };
  const openMuteNotificationsDialog = () => {
    setIsMuteNotificationsDialogOpen(true);
  };

  const getRequestPayload = () => {
    const updatedValue = !areNotificationsMuted;
    const time = muteDuration?.value
      ? dayjs().add(muteDuration.value, "minute").valueOf()
      : 0;
    const requestPayload: IMuteNotificationsRequest = {
      id: groupInfo.id,
      settings: {
        mute: {
          value: updatedValue,
          time,
        },
      },
    };
    return requestPayload;
  };

  const onMuteNotification = async () => {
    try {
      const successMessage = `Group has been ${areNotificationsMuted ? "unmuted" : "muted"}`;
      const chatManager =
        selectedOrgUser?.chatManager.getChatManagerForGroupType(
          groupInfo.groupType,
        ) as IPatientChats | INonPatientChats;

      await chatManager.muteNotifications(getRequestPayload());
      enqueueSnackbar(successMessage, { autoHideDuration: 3000 });
    } catch (e) {
      ApiErrorHandler(e, enqueueSnackbar);
    } finally {
      closeMuteNotificationsDialog();
    }
  };

  const onMuteToggle = async () => {
    if (!areNotificationsMuted) {
      openMuteNotificationsDialog();
    } else {
      await onMuteNotification();
    }
  };

  return (
    <>
      <Toggle
        label={tNs("muteNotifications")}
        onToggle={onMuteToggle}
        checked={areNotificationsMuted}
      />
      <MuteNotifications
        isModalOpen={isMuteNotificationsDialogOpen}
        closeModal={closeMuteNotificationsDialog}
        onMute={onMuteNotification}
        setMuteDuration={setMuteDuration}
        muteDuration={muteDuration}
      />
    </>
  );
};

export default MuteNotificationsToggle;
